import { Link } from 'react-router-dom';

export default function About() {
  return (
    <div data-testid="about-page">

      {/* Hero */}
      <section className="pub-section pub-bg-black">
        <div className="pub-container">
          <p className="pub-eyebrow mb-5">About KYRDROS</p>
          <h1 className="pub-heading-hero" style={{ maxWidth: '780px' }}>
            KYRDROS exists to prevent knowledge drift from becoming organizational failure.
          </h1>
        </div>
      </section>

      {/* Why institutional knowledge fragments */}
      <section className="pub-section pub-bg-white">
        <div className="pub-container">
          <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-16 items-start">
            <div>
              <h2 className="pub-heading-section mb-6" style={{ fontSize: 'clamp(24px, 3vw, 36px)' }}>
                Why institutional knowledge becomes fragmented
              </h2>
              <div className="space-y-5 pub-body" style={{ color: '#444', maxWidth: '520px' }}>
                <p>
                  Organizations accumulate policies, procedures, research, training materials, and AI-generated guidance faster than they can verify it. Individual documents are updated independently. New versions are created without retiring old ones. The result is a landscape where multiple sources claim authority on the same question — and no one knows which controls.
                </p>
                <p>
                  This is not a technology failure. It is a governance failure. Version history and evidence are not tracked. Disagreement is resolved informally, without a record. Decision authority is assumed rather than assigned.
                </p>
                <p>
                  When this goes unaddressed long enough, the organization operates on a distributed set of beliefs — not a shared, accountable body of knowledge.
                </p>
              </div>
            </div>

            <div>
              <h2 className="pub-heading-section mb-6" style={{ fontSize: 'clamp(24px, 3vw, 36px)' }}>
                Why version history and evidence matter
              </h2>
              <div className="space-y-5 pub-body" style={{ color: '#444', maxWidth: '520px' }}>
                <p>
                  When a finding is made, the version of the source it was made against matters. Policies change. The claim that was accurate in version 2.0 may be superseded in version 3.1 — or may not. Without version-linked evidence, you cannot know.
                </p>
                <p>
                  KYRDROS keeps every finding attached to the exact source version that supported it. Evidence is never detached from the passage that created it. The audit record preserves which version was active, who reviewed it, what they decided, and when.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Why disagreement must be preserved */}
      <section className="pub-section pub-bg-subtle">
        <div className="pub-container">
          <div style={{ maxWidth: '760px' }}>
            <h2 className="pub-heading-section mb-6">
              Why disagreement must be preserved
            </h2>
            <div className="space-y-5 pub-body" style={{ color: '#444' }}>
              <p>
                When two authoritative sources conflict, the instinct is to resolve it — to pick one, collapse the disagreement, and move on. KYRDROS takes the opposite position.
              </p>
              <p>
                A conflict is information. The Brain does not produce a single answer when evidence is divided. It produces an ABSTAIN finding and routes it to the human authority responsible. The disagreement stays visible until a named person with the standing to decide has recorded a decision.
              </p>
              <p>
                This is not a limitation. It is the mechanism that prevents one source from silently overriding another. Consequential decisions remain with the humans empowered to make them.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How the five modules work together */}
      <section className="pub-section pub-bg-black">
        <div className="pub-container">
          <h2 className="pub-heading-section pub-heading-section-white mb-14" style={{ maxWidth: '540px' }}>
            How Canon, Brain, Evidence, Sentinel, and Audit work together
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                name: 'Canon',
                desc: 'The register of approved sources and their active versions. Canon does not determine what is true — it records what the organization has formally accepted as authoritative.'
              },
              {
                name: 'Brain',
                desc: 'The analysis layer. Brain compares sources, versions, and claims. It identifies changes, conflicts, stale guidance, and missing evidence. When sources conflict, Brain abstains and routes the finding to a human.'
              },
              {
                name: 'Evidence',
                desc: 'The record of support behind every finding. Each finding is linked to the exact passages, versions, and provenance that created it. Evidence cannot be detached or altered after the fact.'
              },
              {
                name: 'Sentinel',
                desc: 'The change detector. Sentinel watches for version updates, new publications, and material changes to known sources. When something changes, it routes a new event into the review queue.'
              },
              {
                name: 'Audit',
                desc: 'The append-only log. Every review, decision, escalation, and approval is recorded in order. Events cannot be edited or deleted. The audit trail is the final accountability record.'
              },
            ].map(m => (
              <div key={m.name} className="pub-q-card" style={{ borderColor: 'rgba(255,255,255,0.1)' }}>
                <p className="font-heading font-bold text-white mb-3" style={{ fontSize: '18px' }}>
                  {m.name}
                </p>
                <p className="pub-body" style={{ color: '#98a2b3', fontSize: '15px', lineHeight: 1.65 }}>
                  {m.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Durahuman */}
      <section className="pub-section pub-bg-white">
        <div className="pub-container">
          <div style={{ maxWidth: '640px' }}>
            <h2 className="pub-heading-section mb-6" style={{ fontSize: 'clamp(24px, 3vw, 36px)' }}>
              KYRDROS is being developed by Durahuman
            </h2>
            <div className="space-y-4 pub-body" style={{ color: '#444' }}>
              <p>
                Durahuman builds tools for organizations that need to know what they know, who decided it, and whether it still holds. KYRDROS is Durahuman's institutional knowledge readiness platform — currently in private preview.
              </p>
              <p>
                KYRDROS is not connected to the public internet, does not ingest external data feeds, and does not make autonomous decisions. It is a governed interface for the knowledge an organization already has.
              </p>
            </div>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/enterprise" className="pub-btn-primary pub-btn-primary-lg" data-testid="about-enterprise-btn">
                Request enterprise access
              </Link>
              <Link to="/" className="pub-btn-ghost pub-btn-ghost-lg" data-testid="about-home-btn">
                Back to product
              </Link>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
}
